import {
  imageApi,
  modifierApi,
  productApi,
  productCategoryApi,
  IMAGE_ENTITY_TYPES,
  IMAGE_TYPES,
  UPSERT_IMAGES_MAX_BATCH,
  type AvailabilityWindow,
  type CreateModifierGroupInput,
  type CategoryPosition,
  type CreateProductInput,
  type ListResult,
  type LocalizedText,
  type MerchantModerationState,
  type ModifierOptionInput,
  type PendingChangeRequest,
  type ProductModifierGroupInput,
  type UpdateModifierGroupInput,
  type UpdateModifierOptionInput,
  type UpdateProductInput,
  type UpsertImageItem,
} from '@noor/eats-api';
import {
  toMenuCategory,
  toMenuCategoryFromLocalized,
  toMenuModifierGroupFromLocalized,
  toMenuProduct,
  toMenuProductFromLocalized,
} from '../lib/map-menu';
import { MENU_PROMOTIONS } from '../config/promotions';
import type {
  LocalizedField,
  Menu,
  MenuCategory,
  MenuModifierGroup,
  MenuProduct,
  ProductModerationFilter,
} from '../model/schema';

// The Menu screen's catalog is assembled from two merchant-API calls —
// categories and the branch's products — because the backend has no single
// "menu" endpoint. Composition happens here so the page still consumes one
// `Menu` object. The two requests are independent, so they are issued
// concurrently.
//
// The sidebar's promotions rail is fixed reference data rather than a query —
// see `config/promotions`.

/** Ids needed to address one branch's menu. */
export interface MenuScope {
  restaurantId: string;
}

/**
 * Server-side narrowing of the catalog, straight from the URL.
 *
 * Both members are sent as query params AND re-applied client-side
 * (`filterProducts`), because only `name` is a documented filter and neither is
 * documented on `products/by-merchant/by-restaurant`. The backend ignoring one
 * is therefore invisible to the merchant — the screen narrows either way — and
 * the day it starts honouring them the client pass simply stops removing
 * anything. It is part of the QUERY KEY, so each filter has its own cache
 * entry rather than one entry being overwritten per keystroke.
 */
export interface MenuFilters {
  /** Case-insensitive substring on the product name (the box in the toolbar). */
  search?: string | undefined;
  /** Moderation state — one of the design's three pills, or all when absent. */
  status?: ProductModerationFilter | undefined;
  /**
   * Narrow the paged product read to one category. Honoured SERVER-side by the
   * branch-scoped route only (`category_id`); on «Все филиалы» and in showcase
   * mode the caller must still narrow the answer itself.
   */
  categoryId?: string | undefined;
  /**
   * Moderation state of the CATEGORIES, from the sidebar's own status select.
   *
   * A separate axis from `status`: the sidebar narrows which categories are
   * listed, while `status` narrows the products inside them. Sent as a query
   * param on the category list (see `ListProductCategoriesParams.state`) and,
   * unlike the product filters, NOT re-applied client-side — it is a paged
   * backend filter with counts that intentionally remain global to all tabs.
   */
  categoryStatus?: ProductModerationFilter | undefined;
  /** When true, fetch published products and categories visible to clients (Режим витрина). */
  showcase?: boolean | undefined;
}

/**
 * Fields accepted when creating a category (the id is server-owned).
 *
 * A create writes TRANSLATIONS, not a resolved string: `POST
 * /product-categories` takes `names` as a list of `v1LocalizedText`.
 */
export interface CreateCategoryInput {
  nameTranslations: LocalizedField;
}

/**
 * Fields accepted when renaming a category.
 *
 * Translations, like the create: `PATCH /product-categories/{id}` takes `name`
 * as a `v1LocalizedText`, so a rename writes all three languages at once. (An
 * earlier version sent one resolved string as `ru` and blanked `uz`/`en`.)
 */
export interface UpdateCategoryInput {
  nameTranslations: LocalizedField;
}

/**
 * Fields accepted when creating/updating a product (the id is server-owned).
 *
 * The editor writes TRANSLATIONS, not a resolved string, so both translation
 * sets are required here while the catalog's display-only `name`/`description`
 * are not part of the input at all.
 */
/** Selling hours as the editor collects them: "HH:MM" strings, either optional. */
export interface AvailabilityWindowInput {
  from?: string;
  to?: string;
}

/** The wire's `availability_window` for an editor window. */
function toAvailabilityWindow(window: AvailabilityWindowInput): AvailabilityWindow {
  return {
    ...(window.from ? { available_from: window.from } : {}),
    ...(window.to ? { available_to: window.to } : {}),
  };
}

export type ProductInput = Omit<
  MenuProduct,
  | 'id'
  | 'name'
  | 'description'
  | 'nameTranslations'
  | 'descriptionTranslations'
  | 'compositionTranslations'
  | 'nutrition'
  | 'imageUrl'
  | 'imageUrls'
  | 'imageIds'
  | 'coverIndex'
  | 'price'
  | 'availableFrom'
  | 'availableTo'
  | 'ownAvailableFrom'
  | 'ownAvailableTo'
> & {
  nameTranslations: LocalizedField;
  descriptionTranslations: LocalizedField;
  compositionTranslations?: LocalizedField | null;
  nutrition?: {
    calories?: number | null;
    fats?: number | null;
    proteins?: number | null;
  } | null;
  /**
   * Price in so'm. On a CREATE it is the product's own price (required by the
   * API — every branch charges it unless it overrides). On an EDIT it is the
   * branch-scoped price and stays optional: omitted = leave the stored price
   * alone. Typed optional for that second case.
   */
  price?: number;
  /**
   * The product's photos as durable storage KEYS, in gallery order (the first
   * is the cover). The read shapes expand keys into absolute URLs and offer no
   * way back, so this carries ONLY freshly-uploaded photos.
   *
   * `undefined` means "leave the stored photos untouched" — the update touches
   * no image endpoint. An empty array means "clear them". The distinction is
   * the whole reason this is not just a `string[]`: sending `[]` for an
   * unchanged product would wipe its gallery.
   */
  imageKeys?: string[];
  /**
   * Ids of the product's ALREADY-STORED photos the merchant kept, in the order
   * they should appear. Only meaningful on an update.
   *
   * Present because a stored photo cannot be re-sent: its `url` is the
   * backend's one-way expansion of a key. Naming the survivors by id is what
   * lets an edit that adds one photo keep the other four in place instead of
   * deleting and re-uploading the whole gallery (which it cannot do — it has no
   * keys for them).
   */
  keptImageIds?: string[];
  /**
   * The dish's selling hours. `undefined` = leave them alone (an edit that did
   * not touch the fields); `null` = remove them, sold all day again; an object
   * = set them. A side left out is unlimited.
   */
  availabilityWindow?: AvailabilityWindowInput | null;
  /**
   * Which photo the merchant starred as the cover, addressed the only way a
   * client can name a photo that does not exist yet:
   *
   * - `{ kind: 'kept', id }` — an already-stored photo, by its server id.
   * - `{ kind: 'new', index }` — the Nth entry of `imageKeys`, whose server id
   *   only exists once the add lands.
   *
   * Carried explicitly because the cover cannot be recovered from the two
   * lists: `imageKeys` and `keptImageIds` are separate, so concatenating them
   * puts every kept photo ahead of every new one. Taking the first of that
   * concatenation meant a freshly-picked cover could never win while any
   * stored photo survived — the merchant starred a new photo and the old one
   * stayed the cover.
   *
   * `undefined` means "don't touch the cover".
   */
  cover?: { kind: 'kept'; id: string } | { kind: 'new'; index: number };
};

/**
 * What a product write actually did.
 *
 * As of API v1.00.155 a merchant's writes may be GATED behind moderation: the
 * request succeeds, but the change is queued for review instead of applied, and
 * the response carries a `pending_change` receipt in place of the entity. That
 * is not an error and must not be reported as one — but it is also not the
 * usual "here is your updated product", so the two are modelled as a
 * discriminated union rather than an optional product the caller might forget
 * to check.
 */
export type ProductWriteOutcome =
  | { status: 'applied'; product: MenuProduct }
  | { status: 'pending'; pendingChange?: PendingChangeRequest; product?: MenuProduct };

/** The same applied/pending split for a category rename. */
export type CategoryWriteOutcome =
  | { status: 'applied'; category: MenuCategory }
  | { status: 'pending'; pendingChange?: PendingChangeRequest };

/**
 * A view-model translation set → the wire's `v1LocalizedText`. Missing members
 * become `''`, which the backend reads as "no translation yet" (it refuses to
 * store an empty value), so a blank Uz/En input clears nothing it shouldn't.
 */
function toLocalizedText(field: LocalizedField | undefined): LocalizedText {
  return { ru: field?.ru ?? '', uz: field?.uz ?? '', en: field?.en ?? '' };
}

/**
 * Read only the menu taxonomy.
 *
 * Categories, promotion filters and category-management screens do not need
 * product cards. Keeping this read separate prevents those routes from walking
 * every page of a large product catalog merely to populate a select.
 */
async function listMenuCategories(
  scope?: MenuScope,
  filters?: Pick<MenuFilters, 'categoryStatus' | 'showcase'>,
  options?: { signal?: AbortSignal },
): Promise<MenuCategory[]> {
  if (filters?.showcase) {
    const params = scope?.restaurantId
      ? { restaurant_id: scope.restaurantId }
      : undefined;
    const first = await productCategoryApi.listPublished(params, options);
    const categories = await listAll(first, (offset, limit) =>
      productCategoryApi.listPublished({ ...params, limit, offset }, options),
    );
    return categories.map(toMenuCategory);
  }

  const params = filters?.categoryStatus
    ? { state: toMerchantModerationState(filters.categoryStatus) }
    : undefined;
  const first = await productCategoryApi.list(params, options);
  const categories = await listAll(first, (offset, limit) =>
    productCategoryApi.list({ ...params, limit, offset }, options),
  );
  return categories.map(toMenuCategory);
}

export const menuApi = {
  listCategories: listMenuCategories,
  reorderCategories(positions: CategoryPosition[]) {
    return productCategoryApi.reorder(positions);
  },

  /** A single page for the scrolling product grid. Keep raw rows for offsets
   * and totals; local filtering must never change pagination progress. */
  async getProductsPage(
    scope: MenuScope | undefined,
    filters: MenuFilters | undefined,
    pagination: { offset: number; limit: number },
    options?: { signal?: AbortSignal },
  ): Promise<ListResult<MenuProduct>> {
    const name = filters?.search?.trim();
    const params = { ...pagination, ...(name ? { name } : {}) };
    const result = filters?.showcase
      ? await productApi.listPublished(
          {
            ...params,
            ...(scope?.restaurantId ? { restaurant_id: scope.restaurantId } : {}),
          },
          options,
        )
      : scope?.restaurantId
        ? await productApi.listByRestaurant(
            scope.restaurantId,
            {
              ...params,
              ...(filters?.status ? { status: filters.status } : {}),
              ...(filters?.categoryId ? { category_id: filters.categoryId } : {}),
            },
            options,
          )
        : await productApi.list(params, options);
    return {
      ...result,
      list: result.list.map((product) => {
        const item = toMenuProduct(product);
        return filters?.showcase ? { ...item, moderation: 'approved' as const } : item;
      }),
    };
  },

  /**
   * Assemble the catalog, narrowed by the URL's filters.
   *
   * SCOPE. `scope.restaurantId` picks the route, and the two are genuinely
   * different reads rather than the same list filtered two ways:
   *
   * - a branch id → `GET /products/by-merchant/by-restaurant?restaurant_id=…`,
   *   that branch's stocked products with ITS price/stock/stop-list overlaid
   *   and `effective_price` resolved against its campaigns. This is the route
   *   the spec names as powering the panel's «Категории» view.
   * - `undefined` («Все филиалы») → `GET /products/by-merchant`, every product
   *   across the merchant, deduplicated, with NO `restaurant_id` on the wire.
   *   Here `price` is the LOWEST across the branches stocking each product and
   *   there is no per-branch overlay, so the card shows a starting price with
   *   no discount or stop-list badge — those are branch-local facts «Все» has
   *   no single answer for.
   *
   * The merchant chooses between them with the toolbar's branch selector, so
   * neither scope is hidden state: the grid says which branch it is pricing.
   *
   * FILTERS. `name` goes to the server on both routes. `status` is forwarded
   * ONLY on the branch-scoped one — `products/by-merchant` accepts `name` +
   * pagination only, and the gateway rejects unknown query params rather than
   * ignoring them, so sending it there would fail the whole request.
   * `filterProducts` re-applies both to the answer regardless, which is what
   * keeps the pills working on «Все» and covers a backend that ignores `name`.
   *
   * Categories are NOT narrowed, on either scope. They are the sidebar's own
   * merchant-wide taxonomy, and hiding a category because a search matched
   * none of its products would make navigation flicker as the merchant types.
   */
  async get(
    scope?: MenuScope,
    filters?: MenuFilters,
    options?: { signal?: AbortSignal },
  ): Promise<Menu> {
    const isShowcase = Boolean(filters?.showcase);
    const search = filters?.search?.trim() ?? '';
    const restaurantId = scope?.restaurantId;

    if (isShowcase) {
      const productParams = {
        ...(restaurantId ? { restaurant_id: restaurantId } : {}),
        ...(search ? { name: search } : {}),
      };

      const [categories, products] = await Promise.all([
        listMenuCategories(scope, filters, options),
        (async () => {
          const first = await productApi.listPublished(productParams, options);
          return listAll(first, (offset, limit) =>
            productApi.listPublished({ ...productParams, limit, offset }, options),
          );
        })(),
      ]);

      return {
        promotions: [...MENU_PROMOTIONS],
        categories,
        products: filterProducts(
          products.map((p) => {
            const item = toMenuProduct(p);
            // In Vitrina mode, moderation state from published endpoint is empty / ignored;
            // mark as approved/published so no review badges clutter the showcase.
            return { ...item, moderation: 'approved' };
          }),
          filters,
        ),
      };
    }

    const branchParams = {
      ...(search ? { name: search } : {}),
      ...(filters?.status ? { status: filters.status } : {}),
    };
    const merchantParams = search ? { name: search } : undefined;
    // First pages go out exactly as before (no `limit`/`offset`); the
    // follow-ups inside `listAll` walk the remainder only when `meta.total`
    // says rows are still missing — see below.
    const [categories, products] = await Promise.all([
      // Merchant-wide taxonomy — never branch-filtered in normal mode. The
      // status select narrows its own rows, not the products underneath them.
      listMenuCategories(undefined, filters, options),
      (async () => {
        const first = restaurantId
          ? await productApi.listByRestaurant(restaurantId, branchParams, options)
          : await productApi.list(merchantParams, options);
        return restaurantId
          ? listAll(first, (offset, limit) =>
              productApi.listByRestaurant(
                restaurantId,
                { ...branchParams, limit, offset },
                options,
              ),
            )
          : listAll(first, (offset, limit) =>
              productApi.list({ ...merchantParams, limit, offset }, options),
            );
      })(),
    ]);

    return {
      // A fixed "Скидки и акции" entry point — see `config/promotions`.
      promotions: [...MENU_PROMOTIONS],
      categories,
      products: filterProducts(products.map(toMenuProduct), filters),
    };
  },

  /**
   * The merchant's WHOLE catalog, deduplicated across branches — `GET
   * /products/by-merchant`.
   *
   * Deliberately NOT branch-scoped, unlike {@link get}: this is what the branch
   * editor's product picker lists, and a picker scoped to the branch being
   * edited could only ever offer products that branch already stocks. The
   * token supplies the merchant, so no id is sent, and `price` is the LOWEST
   * across the branches stocking each product — a starting point for the
   * per-branch price the picker then captures, not the branch's own.
   */
  async listMerchantProducts(
    params?: { name?: string },
    options?: { signal?: AbortSignal },
  ): Promise<MenuProduct[]> {
    const first = await productApi.list(params, options);
    const list = await listAll(first, (offset, limit) =>
      productApi.list({ ...params, limit, offset }, options),
    );
    return list.map(toMenuProduct);
  },

  /**
   * One branch's stocked products, with that branch's own price overlaid —
   * `GET /products/by-merchant/by-restaurant`.
   *
   * This is what seeds step 2 when EDITING a branch: the rows must show what
   * the branch actually sells and what it charges, which the merchant-wide
   * read above cannot answer (its price is the cross-branch minimum).
   */
  async listBranchProducts(
    restaurantId: string,
    options?: { signal?: AbortSignal },
  ): Promise<MenuProduct[]> {
    const first = await productApi.listByRestaurant(restaurantId, undefined, options);
    const list = await listAll(first, (offset, limit) =>
      productApi.listByRestaurant(restaurantId, { limit, offset }, options),
    );
    return list.map(toMenuProduct);
  },

  /**
   * The merchant's reusable modifier-group catalogue — `GET /modifier-groups`.
   *
   * Groups are merchant-wide entities now, so this is not branch-scoped. The
   * branch only matters when a group is attached to a product for moderation
   * filtering, which happens on write.
   */
  async listModifierGroups(options?: {
    signal?: AbortSignal;
  }): Promise<MenuModifierGroup[]> {
    const { list } = await modifierApi.list(undefined, options);
    return list.map(toMenuModifierGroupFromLocalized);
  },

  /**
   * Read one product, for seeding the editor.
   *
   * Merchant-wide (`GET /products/{id}/by-merchant`) with NO `restaurant_id`
   * on the wire — the branch overlay read
   * (`.../by-merchant/by-restaurant?restaurant_id=…`) is intentionally not
   * used here. The edit writes `price` back through the shared PATCH (see
   * {@link updateProduct}), so seeding from the shared record keeps read and
   * write on the same price.
   *
   * ONE request. `GET /products/{id}/by-merchant` answers with
   * `v1LocalizedProduct` — every translation at once — which is new in API
   * v1.00.155. It replaces the three concurrent `x-locale` reads this used to
   * fan out (CLAUDE.md #12): with a real localized merchant read that
   * workaround is obsolete, and it was also lossy — a `uz` value identical to
   * `ru` could not be told apart from an untranslated fallback, so a genuine
   * translation that happened to match Russian was dropped.
   *
   * `scope` is retained so callers (and the query key) keep passing the
   * branch context, but it is not sent.
   */
  async getProduct(_scope: MenuScope, id: MenuProduct['id']): Promise<MenuProduct> {
    const product = await productApi.get(String(id));
    return toMenuProductFromLocalized(product);
  },

  /**
   * Read one category's translations, for seeding the rename dialog.
   *
   * ONE request, same shape as {@link getProduct}: `GET
   * /product-categories/{id}/by-merchant` answers with every translation at
   * once. It replaces the three concurrent `x-locale` reads this used to fan
   * out — that workaround was also lossy, dropping a genuine `uz`/`en`
   * translation that happened to match Russian.
   */
  async getCategory(id: MenuCategory['id']): Promise<MenuCategory> {
    const category = await productCategoryApi.get(String(id));
    return toMenuCategoryFromLocalized(category);
  },

  /**
   * Create a category. `names` is a LIST of translation sets, so one category
   * is sent as a single-element list and unwrapped back to one category here.
   */
  async createCategory(input: CreateCategoryInput): Promise<MenuCategory> {
    const [created] = await menuApi.createCategories([input]);
    if (!created) throw new Error('Category create returned no category');
    return created;
  },

  /**
   * Create SEVERAL categories in one request.
   *
   * `POST /product-categories` takes `names` as a list, so a merchant staging
   * three categories is one call rather than three — which also makes it
   * all-or-nothing. That matters for the mobile sheet's «Добавить ещё
   * категорию» repeater: a per-category loop could half-succeed, leaving the
   * merchant to work out which blocks landed and re-enter only the rest.
   */
  async createCategories(inputs: CreateCategoryInput[]): Promise<MenuCategory[]> {
    const { list } = await productCategoryApi.create(
      inputs.map((input) => toLocalizedText(input.nameTranslations)),
    );
    return list.map(toMenuCategoryFromLocalized);
  },

  /**
   * Rename a category in every language. The PATCH body's `name` is a
   * `v1LocalizedText`, so all three translations are written in one call —
   * the same shape the create takes.
   *
   * A blank `uz`/`en` goes as `''`, which the backend reads as "not provided"
   * and leaves untouched rather than blanking an existing translation.
   *
   * The PATCH may answer with a `pending_change` receipt instead of the
   * updated category when the merchant's writes are gated behind moderation —
   * a SUCCESS that carries no entity, reported as its own outcome (the same
   * split as {@link updateProduct}). Treating it as an error is exactly how a
   * queued rename used to surface as a failure toast.
   */
  async updateCategory(
    id: MenuCategory['id'],
    input: UpdateCategoryInput,
  ): Promise<CategoryWriteOutcome> {
    const { category, pending_change: pendingChange } = await productCategoryApi.update(
      String(id),
      { name: toLocalizedText(input.nameTranslations) },
    );
    if (!category) {
      return { status: 'pending', ...(pendingChange ? { pendingChange } : {}) };
    }
    return { status: 'applied', category: toMenuCategoryFromLocalized(category) };
  },

  deleteCategory(id: MenuCategory['id']): Promise<void> {
    return productCategoryApi.remove(String(id));
  },

  /**
   * Soft-delete a product — `DELETE /products/{id}`.
   *
   * This retires the merchant-owned product outright, in every branch. Removing
   * it from ONE branch is a different operation (dropping that branch's overlay
   * via `removeFromRestaurants`, which the editor's branch multi-select does).
   */
  deleteProduct(id: MenuProduct['id']): Promise<void> {
    return productApi.remove(String(id));
  },

  /**
   * Create a merchant-owned product. Branch overlays are deliberately NOT sent
   * in this POST body: `restaurants` is no longer accepted by the gateway here,
   * and branch-local stocking/prices live on their dedicated overlay routes.
   */
  async createProduct(
    _scope: MenuScope,
    input: ProductInput,
  ): Promise<ProductWriteOutcome> {
    const body: CreateProductInput = {
      product_category_id: String(input.categoryId),
      name: toLocalizedText(input.nameTranslations),
      description: toLocalizedText(input.descriptionTranslations),
      ...(input.compositionTranslations
        ? { composition: toLocalizedText(input.compositionTranslations) }
        : {}),
      ...(input.nutrition ? { nutrition: input.nutrition } : {}),
      // FLAT `string[]` of storage keys, never display URLs — see
      // `ProductInput`. Create is the one write that still takes the gallery
      // inline; updates go through the per-image endpoints (v1.00.141).
      ...(input.imageKeys?.length ? { images: input.imageKeys } : {}),
      // Always send `is_always_ready` (never omit it): the wire defaults it to
      // false, so omitting it on an update-by-recreate would silently clear a
      // merchant's "always ready" flag. `cook_time_seconds` is sent only when a
      // wait actually applies — an always-ready product has none.
      is_always_ready: input.alwaysReady ?? false,
      is_adult: input.isAdult ?? false,
      // The product's own price — required on create (see `CreateProductInput`).
      ...(input.price === undefined ? {} : { price: input.price }),
      ...(input.alwaysReady || input.cookTimeSeconds === undefined
        ? {}
        : { cook_time_seconds: input.cookTimeSeconds }),
      // Each group either links an existing catalogue group by id or describes
      // a new one created in the same transaction (`modifier_groups`,
      // v1.00.227). `new_group` takes LocalizedText names, so all three
      // languages land in THIS request — the follow-up translation pass the
      // old plain-string inline field forced is gone. Omitted when the editor
      // collected none.
      ...(input.modifiers?.length
        ? { modifier_groups: input.modifiers.map(toProductModifierGroupInput) }
        : {}),
      // OFD fiscalization codes. Omitted when the merchant left them blank —
      // the create validates `marked_code` against `has_marked_code`, so an
      // empty string would be a value the product does not have.
      ...(input.ikpuCode ? { ikpu_code: input.ikpuCode } : {}),
      ...(input.unitCode ? { unit_code: input.unitCode } : {}),
      // Omitted = the brand's rate; 0 is a real "no VAT", hence not a truthy test.
      ...(input.vatPercent === undefined ? {} : { vat_percent: input.vatPercent }),
      ...(input.measureAmount ? { measure_amount: input.measureAmount } : {}),
      // Nothing to clear on a create, so `null` and `undefined` both omit it.
      ...(input.availabilityWindow
        ? { availability_window: toAvailabilityWindow(input.availabilityWindow) }
        : {}),
      // Display unit for the storefront and merchant app; omitted for per-item
      // goods (UNSPECIFIED is the wire default anyway).
      ...(input.measurementType ? { measurement_type: input.measurementType } : {}),
      ...(input.hasMarkedCode
        ? {
            has_marked_code: true,
            ...(input.markedCode ? { marked_code: input.markedCode } : {}),
          }
        : {}),
    };
    const { product, restaurantProducts } = await productApi.create(body);
    const mapped = toMenuProductFromLocalized(
      product,
      restaurantProducts.map((overlay) => overlay.restaurant_id),
    );
    // The create body has no such field — the switch only exists on its own
    // route, so a dish created with its modifiers off is switched right after.
    if (input.modifiersDisabled !== undefined) {
      await setProductModifiersDisabled(mapped.id, input.modifiersDisabled);
    }
    const created: MenuProduct =
      input.modifiersDisabled === undefined
        ? mapped
        : { ...mapped, modifiersDisabled: input.modifiersDisabled };

    // A create is never gated — there is no prior record to moderate — so this
    // always applies, and the outcome is uniform with `updateProduct`'s.
    return { status: 'applied', product: created };
  },

  /**
   * Update a product. The shared record (translations, category, cook time),
   * the gallery (per-image endpoints since v1.00.141) and the per-branch
   * overlays (price, which branches stock it) live on different endpoints, so
   * an edit fans out: patch the product, replace the photos if re-picked, then
   * reconcile the branch set.
   *
   * The PATCH may answer with a `pending_change` receipt instead of the updated
   * product when the merchant's writes are gated behind moderation (v1.00.155).
   * That is a SUCCESS, so it is reported as one — see {@link ProductWriteOutcome}.
   */
  async updateProduct(
    scope: MenuScope,
    id: MenuProduct['id'],
    input: ProductInput,
  ): Promise<ProductWriteOutcome> {
    const body: UpdateProductInput = {
      name: toLocalizedText(input.nameTranslations),
      description: toLocalizedText(input.descriptionTranslations),
      ...(input.compositionTranslations === undefined
        ? {}
        : {
            composition:
              input.compositionTranslations === null
                ? null
                : toLocalizedText(input.compositionTranslations),
          }),
      ...(input.nutrition === undefined ? {} : { nutrition: input.nutrition }),
      ...(input.price === undefined ? {} : { price: input.price }),
      category_id: String(input.categoryId),
      // Sent even though `ProductServiceUpdateProductBody` omits it — see
      // `UpdateProductInput`. Omitting it made the toggle unsavable: it could
      // only ever be set at create time, so unchecking (or checking) it on an
      // existing product silently did nothing.
      is_always_ready: input.alwaysReady ?? false,
      is_adult: input.isAdult ?? false,
      // An always-ready product has no wait, so its cook time is patched to 0
      // rather than left at whatever it was before the toggle was turned on.
      cook_time_seconds: input.alwaysReady ? 0 : (input.cookTimeSeconds ?? 0),
      // Always sent so switching a weighed product back to per-item reaches the
      // wire (UNSPECIFIED) instead of leaving the old unit in place.
      measurement_type: input.measurementType ?? 'MEASUREMENT_TYPE_UNSPECIFIED',
      // NO `modifiers`: v1.00.227 removed the inline field from the PATCH
      // outright. A dish's groups change through the dedicated attach/detach
      // and catalogue endpoints instead — see `reconcileModifierGroups` below,
      // which still honours the "absent input = don't touch" contract.
      ...toFiscalPatch(input),
      // `undefined` = key absent = hours untouched; `null` reaches the wire as
      // an explicit null, which is how the dish goes back to "sold all day".
      ...(input.availabilityWindow === undefined
        ? {}
        : {
            availability_window:
              input.availabilityWindow === null
                ? null
                : toAvailabilityWindow(input.availabilityWindow),
          }),
    };
    const { product: updated, pending_change: pendingChange } = await productApi.update(
      String(id),
      body,
    );

    // The modifiers switch is an operational toggle on its own route, outside
    // moderation (its answer never carries a `pending_change`), so it applies
    // even when the PATCH above was queued — like a stop-list, it is not
    // content a moderator reviews. `undefined` = the merchant did not touch it.
    if (input.modifiersDisabled !== undefined) {
      await setProductModifiersDisabled(id, input.modifiersDisabled);
    }

    // A gated update is queued, not applied: there is no updated product to
    // read the stored gallery from, and following up with image or overlay
    // writes would push changes the moderator never saw. Stop here and report
    // the receipt so the UI can say the edit is awaiting review.
    if (!updated) {
      // The modifier endpoints are gated on their own, so the merchant's
      // modifier edits still go out (and queue their own requests) — skipping
      // them silently dropped the change. The stored groups to diff against
      // come from a fresh read, since the receipt carries no product.
      if (input.modifiers !== undefined) {
        const stored = await menuApi.getProduct(scope, id);
        await reconcileModifierGroups(
          scope,
          String(id),
          input.modifiers,
          stored.modifiers ?? [],
        );
      }
      return { status: 'pending', ...(pendingChange ? { pendingChange } : {}) };
    }

    // Gallery reconciliation — via the Image Service (the update body has no
    // `images` field, and the gateway rejects unknown fields rather than
    // ignoring them). `imageKeys === undefined` means "leave the stored gallery
    // alone" and touches nothing.
    //
    // Photos the merchant KEPT are left in place rather than deleted and
    // re-added: a stored photo's URL is a one-way expansion of its key, so the
    // client has no key to re-upload it with — deleting it would lose it for
    // good. Only genuinely-removed photos are deleted.
    if (input.imageKeys !== undefined) {
      // Only ids the product ACTUALLY has count as kept. The editor's gallery
      // can carry ids the server does not: a synthetic `existing-N` placeholder
      // when the drawer was seeded from a catalog card (which has a cover URL
      // but no gallery ids), or a real id for a photo deleted since the drawer
      // opened. Intersecting with the stored gallery is what keeps such an id
      // out of a promote item, which the server rejects with `image does not
      // belong to this product` (4100) — failing the whole batch, and with it a
      // save that was otherwise fine.
      const storedIds = new Set(
        updated.images.map((image) => image.id).filter((imageId) => imageId !== ''),
      );
      const kept = new Set(
        (input.keptImageIds ?? []).filter((imageId) => storedIds.has(imageId)),
      );
      // A submit that names survivors but resolves NONE of them is not a
      // "delete everything" instruction — it is a gallery the editor could not
      // address, which is exactly the catalog-card seed (all `existing-N`)
      // submitted before the by-id read landed. Deleting on that reading would
      // destroy every stored photo irrecoverably, since a stored URL cannot be
      // re-uploaded. An explicit clear still works: it sends NO kept ids at all.
      const namedSurvivors = (input.keptImageIds ?? []).length > 0;
      const resolvedNone = namedSurvivors && kept.size === 0;
      const removed = resolvedNone
        ? []
        : updated.images.filter((image) => image.id !== '' && !kept.has(image.id));
      // Every removal names a specific photo, so each goes as a `common`
      // delete narrowed by `image_id` — the one selector that can match several
      // photos. Deleting the cover promotes the next by position, so a product
      // with photos left is never left coverless.
      await Promise.all(
        removed.map((image) =>
          imageApi.remove(
            IMAGE_ENTITY_TYPES.product,
            String(id),
            IMAGE_TYPES.common,
            image.id,
          ),
        ),
      );

      // Which NEW photo the merchant starred, if any. Named by its index into
      // `imageKeys` because a new photo has no server id until its write lands.
      const newCoverIndex = input.cover?.kind === 'new' ? input.cover.index : undefined;

      // The whole gallery in ONE call: each new key is an item, and the starred
      // one is typed MAIN while the rest are COMMON. This is what replaced the
      // old add-one-at-a-time loop plus a follow-up `set-main` — the batch is
      // atomic, so a gallery can no longer half-land.
      //
      // Order still follows the items' order within the batch, so `imageKeys`
      // is NOT reordered to hoist the cover: doing so would silently rearrange
      // the merchant's gallery as a side effect of starring a photo.
      const items: UpsertImageItem[] = input.imageKeys.map((link, index) => ({
        image_type: index === newCoverIndex ? IMAGE_TYPES.main : IMAGE_TYPES.common,
        link,
      }));

      // A KEPT photo promoted to cover is a pure role swap — no content
      // changes, so it carries an `image_id` and NO `link`. This is the only
      // way to restar a stored photo at all: its key is unrecoverable from the
      // display URL, so it can never be re-sent as content.
      //
      // Skipped when the star is unaddressable (a synthetic `existing-N` from a
      // catalog-card seed, or a photo deleted since the drawer opened) — the
      // server rejects such an id and would fail the whole batch.
      if (input.cover?.kind === 'kept' && kept.has(input.cover.id)) {
        items.push({ image_type: IMAGE_TYPES.main, image_id: input.cover.id });
      }

      // Chunked: the endpoint takes at most `UPSERT_IMAGES_MAX_BATCH` items,
      // the same cap the presign batch that produced these keys obeys.
      // Sequential per chunk, since `position` follows write order.
      for (let i = 0; i < items.length; i += UPSERT_IMAGES_MAX_BATCH) {
        await imageApi.upsert(
          IMAGE_ENTITY_TYPES.product,
          String(id),
          items.slice(i, i + UPSERT_IMAGES_MAX_BATCH),
        );
      }
    }

    // Branch overlays on an EDIT:
    // The price is updated directly on the product API (ProductService),
    // so no request to PATCH /products/{id}/restaurant-products is needed.
    const branchIds = resolveBranchIds(scope, input.branchIds);
    const saved: MenuProduct = {
      ...toMenuProductFromLocalized(updated, branchIds),
      ...(input.price !== undefined ? { price: input.price } : {}),
      // The PATCH answered BEFORE the switch was thrown, so its echo is stale.
      ...(input.modifiersDisabled !== undefined
        ? { modifiersDisabled: input.modifiersDisabled }
        : {}),
    };

    // The PATCH no longer touches modifiers at all (v1.00.227), so the
    // editor's groups are reconciled against the stored ones through the
    // dedicated endpoints. `undefined` still means "don't touch" — an editor
    // that never collected modifiers must not detach every group. The PATCH
    // response carries the CURRENT stored groups, which is exactly the state
    // to diff against. Skipped when the update was gated — that path returns
    // above, and these writes would push changes past the same queue.
    if (input.modifiers !== undefined) {
      const queued = await reconcileModifierGroups(
        scope,
        String(id),
        input.modifiers,
        saved.modifiers ?? [],
      );
      // The product itself was applied, but a modifier change waits for a
      // moderator — say so instead of reporting a plain success.
      if (queued) return { status: 'pending', product: saved };
    }

    return { status: 'applied', product: saved };
  },
};

/**
 * Throw one dish's modifiers switch (`PUT /merchant/products/modifiers-disabled`
 * with a one-element list — the route has no per-product form).
 *
 * The answer lists the id under `skipped_product_ids` when the dish no longer
 * exists (deleted while the editor was open). That is a failed save, not a
 * quiet no-op: the merchant asked for a state the catalog cannot hold.
 */
async function setProductModifiersDisabled(
  productId: MenuProduct['id'],
  disabled: boolean,
): Promise<void> {
  const id = String(productId);
  const { skipped_product_ids: skipped } = await productApi.setModifiersDisabled(
    [id],
    disabled,
  );
  if (skipped.includes(id)) {
    throw new Error(`Product ${id} no longer exists`);
  }
}

/**
 * Page size for the catalog's follow-up reads. Only reached when the first
 * (unpaged) response's `meta.total` exceeds the rows it carried, so small
 * catalogs never pay for it.
 */
const MENU_PAGE_LIMIT = 100;

/**
 * Collect every page of a `{ list, meta }` read.
 *
 * The catalog screen has no pager, so it must show the WHOLE result set —
 * but the list endpoints are limit/offset-paged server-side, and an unpaged
 * request answers with just the default first page. The first page is the
 * caller's (unchanged, so its wire params stay exactly as they were); the
 * follow-ups re-issue it with `limit`/`offset` until `meta.total` rows are
 * in. A single-page answer therefore costs no extra request.
 *
 * Terminates: every follow-up that returns rows strictly shrinks the
 * remaining count, and an empty page stops the walk — without that, a backend
 * ignoring `offset` would repeat the same page forever.
 */
async function listAll<T>(
  first: ListResult<T>,
  fetchPage: (offset: number, limit: number) => Promise<ListResult<T>>,
): Promise<T[]> {
  const items = [...first.list];
  let offset = first.list.length;
  // Some gateway/proxy versions silently ignore pagination parameters. Keep
  // the catalog from issuing an unbounded stream of identical requests when
  // `meta.total` claims there are more rows than the endpoint can return.
  const seenPageSignatures = new Set([JSON.stringify(first.list)]);
  while (items.length < first.meta.total) {
    const requestedLimit = Math.min(MENU_PAGE_LIMIT, first.meta.total - items.length);
    const page = await fetchPage(offset, requestedLimit);
    if (page.list.length === 0) break;
    const pageSignature = JSON.stringify(page.list);
    // A backend can alternate between cached pages instead of repeating the
    // immediately previous one. Stop on any page seen before; otherwise a
    // bad total can keep this loop alive until the browser becomes unusable.
    if (seenPageSignatures.has(pageSignature)) break;
    items.push(...page.list);
    offset += page.list.length;
    seenPageSignatures.add(pageSignature);
  }
  return items;
}

function toMerchantModerationState(
  status: ProductModerationFilter,
): MerchantModerationState {
  if (status === 'draft') return 'MERCHANT_MODERATION_STATE_DRAFT';
  if (status === 'pending') return 'MERCHANT_MODERATION_STATE_ON_MODERATION';
  if (status === 'approved') return 'MERCHANT_MODERATION_STATE_APPROVED';
  return 'MERCHANT_MODERATION_STATE_REJECTED';
}

/**
 * The OFD fiscalization codes as a PATCH fragment.
 *
 * Every field is `nil = don't touch` on the wire, so an untouched code is
 * omitted rather than sent blank — sending `''` would read as a real value and
 * wipe a stored classifier. `marked_code` is only meaningful while
 * `has_marked_code` is on, so turning the flag off clears the code in the same
 * patch instead of leaving an orphaned Data Matrix behind.
 */
function toFiscalPatch(input: ProductInput): Partial<UpdateProductInput> {
  const hasMarkedCode = input.hasMarkedCode ?? false;
  return {
    ...(input.ikpuCode === undefined ? {} : { ikpu_code: input.ikpuCode }),
    ...(input.unitCode === undefined ? {} : { unit_code: input.unitCode }),
    ...(input.vatPercent === undefined ? {} : { vat_percent: input.vatPercent }),
    // Always sent so a cleared box reaches the wire: a number sets it, 0 clears
    // it back to "unknown" (the API's own zero value).
    measure_amount: input.measureAmount ?? 0,
    ...(input.hasMarkedCode === undefined ? {} : { has_marked_code: hasMarkedCode }),
    ...(hasMarkedCode
      ? input.markedCode === undefined
        ? {}
        : { marked_code: input.markedCode }
      : { marked_code: '' }),
  };
}

/**
 * One editor group → the `modifier_groups` entry on `POST /products`
 * (v1.00.227): an existing catalogue group is LINKED by id, a new one is
 * described inline and created in the same transaction. `sort_order` is
 * 1-based — protobuf-JSON omits zero values, so a 0 for the first row would
 * simply vanish from the request.
 */
function toProductModifierGroupInput(
  group: MenuModifierGroup,
  index: number,
): ProductModifierGroupInput {
  return {
    sort_order: index + 1,
    ...(group.id !== undefined
      ? { group_id: group.id }
      : { new_group: toCreateModifierGroupInput(group) }),
  };
}

/**
 * An editor group → `v1CreateModifierGroupRequest`. Names are `LocalizedText`,
 * so every language lands in the create itself.
 *
 * `max_select` is OMITTED (not zeroed) when the merchant set no ceiling, and
 * `max_options` is never sent — that caps how many options the group may hold,
 * which the editor does not ask about.
 */
function toCreateModifierGroupInput(group: MenuModifierGroup): CreateModifierGroupInput {
  return {
    name: toLocalizedText(group.name),
    min_select: group.minSelect,
    ...(group.maxSelect === undefined ? {} : { max_select: group.maxSelect }),
    options: group.options.map(toModifierOptionInput),
  };
}

/** An editor option row → `v1ModifierOptionInput` (1-based `sort_order`). */
function toModifierOptionInput(
  option: MenuModifierGroup['options'][number],
  index: number,
): ModifierOptionInput {
  return {
    name: toLocalizedText(option.name),
    price: option.priceDelta,
    sort_order: index + 1,
    ...(option.ikpuCode ? { ikpu_code: option.ikpuCode } : {}),
    ...(option.unitCode ? { unit_code: option.unitCode } : {}),
    ...(option.vatPercent === undefined ? {} : { vat_percent: option.vatPercent }),
  };
}

/**
 * Bring a dish's stored modifier groups in line with the editor's rows.
 *
 * The product PATCH lost its inline `modifiers` field in v1.00.227, so an edit
 * fans out over the dedicated routes instead, diffing DESIRED (the form)
 * against STORED (what the PATCH response echoed back) by server id:
 *
 *   - a stored group missing from the form  → DETACH from this dish (the
 *     catalogue group survives — other dishes may carry it)
 *   - a form group with no id               → ATTACH with `new_group`
 *   - a form group with an id               → PATCH the group's own fields,
 *     then diff its options: add (no id) / patch (changed) / delete (gone)
 *
 * Groups are merchant-wide catalogue entities now, so a group/option PATCH
 * here is visible on EVERY dish carrying the group — which is the point: the
 * editor edits "Соусы", not this dish's private copy of it.
 *
 * NOT best-effort: these writes ARE the merchant's modifier edit (unlike the
 * old translation follow-up, which merely embellished an edit the PATCH had
 * already applied). Failures are collected — every independent write is still
 * attempted — and then thrown, so a half-applied edit surfaces as an error
 * instead of a silent success. A gated write answering `pending_change` is a
 * SUCCESS (queued), not a failure.
 */
async function reconcileModifierGroups(
  scope: MenuScope,
  productId: string,
  desired: MenuModifierGroup[],
  stored: MenuModifierGroup[],
): Promise<boolean> {
  const writes: Promise<unknown>[] = [];

  // The merchant-wide modifier catalogue, read AT MOST ONCE per save and only
  // if a group is attached by id (see below). Memoized on the promise rather
  // than the value so two attaches in one submit share a single request.
  let cataloguePromise: Promise<MenuModifierGroup[]> | undefined;
  const catalogueGroups = (): Promise<MenuModifierGroup[]> => {
    cataloguePromise ??= menuApi.listModifierGroups();
    return cataloguePromise;
  };

  const desiredIds = new Set(
    desired.map((group) => group.id).filter((groupId) => groupId !== undefined),
  );
  const storedById = new Map(
    stored
      .filter((group) => group.id !== undefined)
      .map((group) => [group.id, group] as const),
  );

  for (const group of stored) {
    if (group.id !== undefined && !desiredIds.has(group.id)) {
      writes.push(productApi.detachModifierGroup(productId, group.id));
    }
  }

  desired.forEach((group, index) => {
    const groupId = group.id;
    if (groupId === undefined) {
      writes.push(
        productApi.attachModifierGroup(productId, {
          sort_order: index + 1,
          new_group: toCreateModifierGroupInput(group),
          // The moderation queue's branch filter — not a scope (groups are
          // merchant-wide), just what lets the request be found per branch.
          restaurant_id: scope.restaurantId,
        }),
      );
      return;
    }

    const storedGroup = storedById.get(groupId);
    if (storedGroup === undefined) {
      // An id the dish does not carry yet — a catalogue group the merchant
      // picked from «Выбрать существующий» in the modifier step. Attaching by
      // id is the defined way to put it on the dish.
      //
      // The attach must land BEFORE any edit to the group, so it is awaited
      // rather than queued alongside the rest: adding an option to a group the
      // product does not carry yet is a race the server can lose.
      const attach = productApi.attachModifierGroup(productId, {
        sort_order: index + 1,
        group_id: groupId,
        restaurant_id: scope.restaurantId,
      });

      // …and the merchant may have EDITED it in the same submit — the step
      // seeds the picked group's own name, bounds and options into the form,
      // so adding a variant or changing the «3шт» cap right after picking it is
      // the ordinary flow. Attaching alone dropped every one of those edits on
      // the floor: the new option never reached the wire, and reopening the
      // product showed the catalogue's untouched group, cap included.
      //
      // The baseline has to be the CATALOGUE's copy of the group, not the form
      // row — diffing the row against itself finds no change, so a renamed or
      // repriced existing option would be just as silently dropped as a new one
      // was. `catalogueGroups` is read once, lazily, and only when a submit
      // actually attaches something.
      writes.push(
        attach.then(async (attached) => {
          const catalogue = await catalogueGroups();
          const baseline = catalogue.find((entry) => entry.id === groupId);
          // No catalogue row means the group was deleted between the pick and
          // the save. The attach above would have failed in that case, so this
          // is unreachable in practice; treating it as "nothing stored" keeps
          // the follow-up honest rather than guessing.
          const stored: MenuModifierGroup = baseline ?? { ...group, options: [] };

          const followUps: Promise<unknown>[] = [];
          const groupPatch = toModifierGroupPatch(group, stored);
          if (groupPatch !== undefined) {
            followUps.push(
              modifierApi.update(groupId, {
                ...groupPatch,
                restaurant_id: scope.restaurantId,
              }),
            );
          }
          reconcileModifierOptions(scope, groupId, group, stored, followUps);
          return queuedByModeration([attached, ...(await Promise.all(followUps))])
            ? QUEUED_RECEIPT
            : undefined;
        }),
      );
      return;
    }

    const groupPatch = toModifierGroupPatch(group, storedGroup);
    if (groupPatch !== undefined) {
      writes.push(
        modifierApi.update(groupId, { ...groupPatch, restaurant_id: scope.restaurantId }),
      );
    }

    reconcileModifierOptions(scope, groupId, group, storedGroup, writes);
  });

  const results = await Promise.allSettled(writes);
  const failures = results.filter(
    (result): result is PromiseRejectedResult => result.status === 'rejected',
  );
  if (failures.length > 0) {
    // Re-throw the first real failure so the API error message survives to the
    // toast; the count in parentheses says whether it was one write or many.
    throw failures[0]?.reason instanceof Error
      ? failures[0].reason
      : new Error(`Modifier reconciliation failed (${failures.length} writes)`);
  }
  return queuedByModeration(
    results.map((result) => (result.status === 'fulfilled' ? result.value : undefined)),
  );
}

/** Stand-in receipt for a follow-up chain that queued at least one request. */
const QUEUED_RECEIPT = { pending_change: { queued: true } };

/** Whether any write answered with a moderation `pending_change` receipt. */
function queuedByModeration(results: readonly unknown[]): boolean {
  return results.some(
    (result) =>
      typeof result === 'object' &&
      result !== null &&
      'pending_change' in result &&
      Boolean(result.pending_change),
  );
}

/**
 * The group-level PATCH, or `undefined` when nothing on the group itself
 * changed. Dropping a ceiling goes through `clear_max_select` — the wire
 * rejects `0` as a stand-in for "no cap".
 */
function toModifierGroupPatch(
  desired: MenuModifierGroup,
  stored: MenuModifierGroup,
): UpdateModifierGroupInput | undefined {
  const patch: UpdateModifierGroupInput = {
    ...(localizedFieldEquals(desired.name, stored.name)
      ? {}
      : { name: toLocalizedText(desired.name) }),
    ...(desired.minSelect === stored.minSelect ? {} : { min_select: desired.minSelect }),
    ...(desired.maxSelect === stored.maxSelect
      ? {}
      : desired.maxSelect === undefined
        ? { clear_max_select: true }
        : { max_select: desired.maxSelect }),
  };
  return Object.keys(patch).length > 0 ? patch : undefined;
}

/** Queue the option-level writes for one kept group onto `writes`. */
function reconcileModifierOptions(
  scope: MenuScope,
  groupId: string,
  desired: MenuModifierGroup,
  stored: MenuModifierGroup,
  writes: Promise<unknown>[],
): void {
  const desiredOptionIds = new Set(
    desired.options
      .map((option) => option.id)
      .filter((optionId) => optionId !== undefined),
  );
  const storedOptionsById = new Map(
    stored.options
      .filter((option) => option.id !== undefined)
      .map((option) => [option.id, option] as const),
  );

  for (const option of stored.options) {
    if (option.id !== undefined && !desiredOptionIds.has(option.id)) {
      writes.push(modifierApi.removeOption(option.id));
    }
  }

  desired.options.forEach((option, index) => {
    const optionId = option.id;
    if (optionId === undefined) {
      writes.push(
        modifierApi.addOption(
          groupId,
          toModifierOptionInput(option, index),
          scope.restaurantId,
        ),
      );
      return;
    }

    const storedOption = storedOptionsById.get(optionId);
    if (storedOption === undefined) return;

    const storedIndex = stored.options.indexOf(storedOption);
    const patch: UpdateModifierOptionInput = {
      ...(localizedFieldEquals(option.name, storedOption.name)
        ? {}
        : { name: toLocalizedText(option.name) }),
      ...(option.priceDelta === storedOption.priceDelta
        ? {}
        : { price: option.priceDelta }),
      ...(storedIndex === index ? {} : { sort_order: index + 1 }),
      ...(option.ikpuCode !== undefined && option.ikpuCode !== storedOption.ikpuCode
        ? { ikpu_code: option.ikpuCode }
        : {}),
      ...(option.unitCode !== undefined && option.unitCode !== storedOption.unitCode
        ? { unit_code: option.unitCode }
        : {}),
      ...(option.vatPercent !== undefined && option.vatPercent !== storedOption.vatPercent
        ? { vat_percent: option.vatPercent }
        : {}),
    };
    if (Object.keys(patch).length > 0) {
      writes.push(
        modifierApi.updateOption(optionId, {
          ...patch,
          restaurant_id: scope.restaurantId,
        }),
      );
    }
  });
}

/** Same stored translations, member by member (absent ≡ empty). */
function localizedFieldEquals(a: LocalizedField, b: LocalizedField): boolean {
  return a.ru === b.ru && (a.uz ?? '') === (b.uz ?? '') && (a.en ?? '') === (b.en ?? '');
}

/**
 * The explicitly selected branch ids, used by edit-only reconciliation paths.
 * Create no longer sends branch overlays in the product POST body.
 */
function resolveSelectedBranchIds(branchIds: string[] | undefined): string[] {
  return (branchIds ?? []).filter((id) => id.length > 0);
}

/**
 * The branches an EDIT applies to. An empty selection means "the branch being
 * edited" rather than "no branches": the editor is scoped to the branch whose
 * overlay it can verify.
 */
function resolveBranchIds(scope: MenuScope, branchIds: string[] | undefined): string[] {
  const selected = resolveSelectedBranchIds(branchIds);
  return selected.length > 0 ? selected : [scope.restaurantId];
}

/**
 * Re-apply the filters the request already asked for, in case the backend did
 * not (see `menuApi.get`). Pure, so it is unit-tested directly.
 *
 * A product with NO `moderation` is dropped by a status filter rather than
 * kept: the pills are exclusive states, and an unknown one is not evidence of
 * membership in the state the merchant asked to see.
 */
export function filterProducts(
  products: MenuProduct[],
  filters?: MenuFilters,
): MenuProduct[] {
  const query = filters?.search?.trim().toLowerCase() ?? '';
  const status = filters?.status;
  if (!query && !status) return products;

  return products.filter((product) => {
    if (status && product.moderation !== status) return false;
    if (query && !product.name.toLowerCase().includes(query)) return false;
    return true;
  });
}
